import React from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import {ActionButton, EButtonAppearance, TActionButtonOption, showModal} from '@edna/components';
import PlayIcon from '@edna/icons/play.svg';

import {PauseIcon} from 'src/icons';
import {parseISO} from 'src/utils';

import ClientBadge from './ClientBadge';
import ConfirmActivatingModal from './ConfirmActivatingModal';
import ConfirmDeactivatingModal from './ConfirmDeactivatingModal';
import {SupportPagesSelector} from './SupportPagesSelector';
import {EClientStatus, TClientStatus} from './definitions';
import * as S from './style';
import {getClientStatus} from './utils';

type TProps = {
  temporaryBlocked?: boolean;
  locked?: boolean;
  unblockAt?: string;
};

export const ClientStatus = React.memo<TProps>(({temporaryBlocked, locked, unblockAt}) => {
  const {t} = useTranslation();
  const {id: supportTenantId} = useParams<'id'>();

  const status = React.useMemo(
    () => getClientStatus(temporaryBlocked, locked),
    [temporaryBlocked, locked],
  );

  const handleDeactivateClient = React.useCallback(() => {
    showModal((closeModal) => <ConfirmDeactivatingModal closeModal={closeModal} />);
  }, []);

  const handleActivateClient = React.useCallback(() => {
    showModal((closeModalProps) => <ConfirmActivatingModal closeModal={closeModalProps} />);
  }, []);

  const activeTenantOptions = [
    {
      content: (
        <>
          <S.OptionIcon as={PauseIcon} />
          <span>{t('ClientStatus:options.moveToDeactivated')}</span>
        </>
      ),
      handler: handleDeactivateClient,
    },
  ];

  const inactiveTenantOptions = [
    {
      content: (
        <>
          <S.OptionIcon as={PlayIcon} />
          <span>{t('ClientStatus:options.moveToActivated')}</span>
        </>
      ),
      handler: handleActivateClient,
    },
  ];

  const hashOptions: Record<TClientStatus, TActionButtonOption[]> = {
    ACTIVE: activeTenantOptions,
    INACTIVE: inactiveTenantOptions,
    BLOCKED: [] as TActionButtonOption[],
  };

  return (
    <div>
      <S.ClientStatusText>{t('ClientStatus:clientStatus')}</S.ClientStatusText>
      <S.Row>
        <ClientBadge status={status}>{t(`ClientStatus:clientStatuses.${status}`)}</ClientBadge>
        {status === EClientStatus.INACTIVE && (
          <S.Date>
            {t('ClientStatus:until', {
              date: parseISO(unblockAt, 'dd.MM.yyyy'),
            })}
          </S.Date>
        )}
        {!!supportTenantId && (
          <ActionButton
            appearance={EButtonAppearance.tertiary}
            size="S"
            overlayPlacementX="left"
            options={hashOptions[status]}
          >
            <S.MoreHorizIcon />
            {t('ClientStatus:changeStatus')}
          </ActionButton>
        )}
        {!!supportTenantId && <SupportPagesSelector tenantId={supportTenantId} />}
      </S.Row>
    </div>
  );
});

ClientStatus.displayName = 'ClientStatus';
